/**
 * Dashboard Service
 * =================
 * Aggregates data for the enhanced trainer dashboard.
 * Provides at-a-glance stats, quick actions counts, and recent activity.
 */

import { supabase } from '../lib/supabase'

// Type-safe RPC wrapper
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const rpc = (fn: string, params: Record<string, unknown>) => supabase.rpc(fn as any, params as any)

/**
 * Dashboard overview statistics
 */
export interface DashboardOverview {
  // Quick action counts
  totalPrograms: number
  activePrograms: number
  completedPrograms: number
  totalVideos: number

  // At-a-glance stats
  activeClients: number
  programsInProgress: number
  overallCompletionRate: number
  needsAttention: number

  // Engagement metrics
  totalViews: number
  totalLikes: number
  recentVideos: number
}

/**
 * Recent activity item
 */
export interface ActivityItem {
  id: string
  type: 'video_watched' | 'task_completed' | 'program_started' | 'program_completed' | 'client_added'
  timestamp: string
  clientName?: string
  clientId?: string
  videoTitle?: string
  videoId?: string
  programTitle?: string
  programId?: string
  description: string
}

/**
 * Client status for compliance tracking
 */
export interface ClientStatus {
  clientId: string
  clientName: string
  email: string
  assignmentId: string
  programTitle: string
  totalTasks: number
  completedTasks: number
  completionRate: number
  lastActivity: string | null
  status: 'on_track' | 'behind' | 'at_risk' | 'no_tasks'
}

/**
 * Program summary for dashboard
 */
export interface ProgramSummary {
  id: string
  title: string
  description: string | null
  visibility: string
  isTemplate: boolean
  itemCount: number
  assignedCount: number
  createdAt: string
  updatedAt: string
}

export const dashboardService = {
  /**
   * Get comprehensive dashboard overview for a provider
   */
  async getDashboardOverview(providerId: string): Promise<DashboardOverview> {
    // Fetch all data in parallel for performance
    const [
      videosResult,
      programsResult,
      assignmentsResult,
      clientsResult,
    ] = await Promise.all([
      // Get videos
      supabase
        .from('videos')
        .select('id, view_count, like_count, created_at')
        .eq('user_id', providerId),
      // Get programs
      supabase
        .from('programs')
        .select('id, title')
        .eq('user_id', providerId),
      // Get assignments with status
      (supabase
        .from('assignments')
        .select('id, status, client_id, program_id')
        .eq('provider_id' as any, providerId) as any),
      // Get active clients
      (supabase
        .from('clients')
        .select('id')
        .eq('provider_id' as any, providerId)
        .eq('status' as any, 'active') as any),
    ])

    // Type assertions for data that needs casting
    const videos = (videosResult.data || []) as { id: string; view_count: number | null; like_count: number | null; created_at: string }[]
    const programs = (programsResult.data || []) as { id: string; title: string }[]
    const assignments = (assignmentsResult.data || []) as { id: string; status: string; client_id: string; program_id: string }[]
    const clients = (clientsResult.data || []) as { id: string }[]

    // Calculate video stats
    const totalViews = videos.reduce((sum, v) => sum + (v.view_count || 0), 0)
    const totalLikes = videos.reduce((sum, v) => sum + (v.like_count || 0), 0)

    // Videos from last 30 days
    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
    const recentVideos = videos.filter(
      (v) => new Date(v.created_at) > thirtyDaysAgo
    ).length

    // Calculate assignment stats
    const activeAssignments = assignments.filter((a) => a.status === 'active')
    const completedAssignments = assignments.filter((a) => a.status === 'completed')

    // Programs with active assignments (in progress)
    const programsWithActiveAssignments = new Set(
      activeAssignments.map((a) => a.program_id)
    )

    // Get completion rate from RPC if available, otherwise estimate
    let overallCompletionRate = 0
    let needsAttention = 0

    try {
      const { data: complianceData } = await rpc('get_provider_compliance_stats', {
        p_provider_id: providerId,
      })
      if (complianceData && Array.isArray(complianceData) && complianceData.length > 0) {
        overallCompletionRate = complianceData[0].overall_completion_rate || 0
        needsAttention = complianceData[0].at_risk_count || 0
      }
    } catch {
      // RPC might not exist yet, use fallback calculation
      // Estimate: clients with < 50% completion in active assignments
      needsAttention = Math.floor(activeAssignments.length * 0.1) // Rough estimate
    }

    return {
      // Quick action counts
      totalPrograms: programs.length,
      activePrograms: activeAssignments.length,
      completedPrograms: completedAssignments.length,
      totalVideos: videos.length,

      // At-a-glance stats
      activeClients: clients.length,
      programsInProgress: programsWithActiveAssignments.size,
      overallCompletionRate,
      needsAttention,

      // Engagement metrics
      totalViews,
      totalLikes,
      recentVideos,
    }
  },

  /**
   * Get recent activity feed for a provider
   */
  async getRecentActivity(
    providerId: string,
    limit = 10
  ): Promise<ActivityItem[]> {
    const activities: ActivityItem[] = []

    // Get provider's video IDs first
    const { data: videos } = await supabase
      .from('videos')
      .select('id, title')
      .eq('user_id', providerId)

    const videoMap = new Map((videos || []).map((v) => [v.id, v.title]))
    const videoIds = Array.from(videoMap.keys())

    if (videoIds.length > 0) {
      // Get recent video views
      const { data: recentViews } = await (supabase
        .from('video_views')
        .select(`
          id,
          created_at,
          completed,
          video_id
        `)
        .in('video_id' as any, videoIds)
        .order('created_at', { ascending: false })
        .limit(limit) as any)

      if (recentViews) {
        for (const view of recentViews as { id: string; created_at: string; completed: boolean; video_id: string }[]) {
          activities.push({
            id: view.id,
            type: view.completed ? 'task_completed' : 'video_watched',
            timestamp: view.created_at,
            videoTitle: videoMap.get(view.video_id) || 'Unknown Video',
            videoId: view.video_id,
            description: view.completed
              ? `Video completed: ${videoMap.get(view.video_id)}`
              : `Video watched: ${videoMap.get(view.video_id)}`,
          })
        }
      }
    }

    // Get recent assignment progress
    const { data: assignments } = await (supabase
      .from('assignments')
      .select('id, program_id')
      .eq('provider_id' as any, providerId) as any)

    const assignmentIds = (assignments || []).map((a: { id: string }) => a.id)

    if (assignmentIds.length > 0) {
      const { data: progressData } = await (supabase
        .from('assignment_progress')
        .select(`
          id,
          created_at,
          completed_at,
          client_id,
          assignment_id
        `)
        .in('assignment_id' as any, assignmentIds)
        .order('created_at', { ascending: false })
        .limit(limit) as any)

      if (progressData) {
        // Get client names
        const clientIds = [...new Set((progressData as any[]).map((p) => p.client_id))]
        const { data: clientsData } = await (supabase
          .from('clients')
          .select('id, full_name')
          .in('id', clientIds) as any)

        const clientMap = new Map<string, string>((clientsData || []).map((c: { id: string; full_name: string }) => [c.id, c.full_name]))

        for (const progress of progressData as { id: string; created_at: string; completed_at: string | null; client_id: string; assignment_id: string }[]) {
          if (progress.completed_at) {
            const completedAt = progress.completed_at
            activities.push({
              id: `progress-${progress.id}`,
              type: 'task_completed',
              timestamp: completedAt,
              clientName: clientMap.get(progress.client_id) || 'Unknown Client',
              clientId: progress.client_id,
              description: `${clientMap.get(progress.client_id) || 'Client'} completed a task`,
            })
          }
        }
      }
    }

    // Sort by timestamp and limit
    return activities
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, limit)
  },

  /**
   * Get client status list for compliance overview
   */
  async getClientStatusList(providerId: string): Promise<ClientStatus[]> {
    // Try using the RPC function first
    try {
      const { data, error } = await rpc('get_client_compliance', {
        p_provider_id: providerId,
      })

      if (!error && data && Array.isArray(data)) {
        return data.map((row: any) => ({
          clientId: row.client_id,
          clientName: row.client_name,
          email: '',
          assignmentId: row.assignment_id,
          programTitle: row.program_title,
          totalTasks: row.total_tasks,
          completedTasks: row.completed_tasks,
          completionRate: row.completion_rate,
          lastActivity: row.last_activity,
          status: row.status as ClientStatus['status'],
        }))
      }
    } catch {
      // RPC doesn't exist yet, fall through to manual calculation
    }

    // Manual fallback
    const { data: assignmentsData } = await (supabase
      .from('assignments')
      .select(`
        id,
        status,
        client:clients (id, full_name, email),
        program:programs (id, title)
      `)
      .eq('provider_id' as any, providerId)
      .eq('status' as any, 'active') as any)

    if (!assignmentsData) return []

    const results: ClientStatus[] = []

    for (const assignment of assignmentsData as any[]) {
      // Get program items count
      const { count: itemCount } = await supabase
        .from('program_items')
        .select('*', { count: 'exact', head: true })
        .eq('program_id', assignment.program.id)

      // Get completed progress count
      const { count: completedCount } = await (supabase
        .from('assignment_progress')
        .select('*', { count: 'exact', head: true })
        .eq('assignment_id' as any, assignment.id)
        .not('completed_at', 'is', null) as any)

      const totalTasks = itemCount || 0
      const completedTasks = completedCount || 0
      const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0

      let status: ClientStatus['status'] = 'no_tasks'
      if (totalTasks > 0) {
        if (completionRate >= 75) status = 'on_track'
        else if (completionRate >= 50) status = 'behind'
        else status = 'at_risk'
      }

      results.push({
        clientId: assignment.client.id,
        clientName: assignment.client.full_name,
        email: assignment.client.email,
        assignmentId: assignment.id,
        programTitle: assignment.program.title,
        totalTasks,
        completedTasks,
        completionRate,
        lastActivity: null,
        status,
      })
    }

    return results.sort((a, b) => a.completionRate - b.completionRate)
  },

  /**
   * Get program summaries for quick actions
   */
  async getProgramSummaries(
    providerId: string,
    options?: { limit?: number; status?: 'active' | 'all' }
  ): Promise<ProgramSummary[]> {
    const { data: programs, error } = await supabase
      .from('programs')
      .select('*')
      .eq('user_id', providerId)
      .order('updated_at', { ascending: false })
      .limit(options?.limit || 10)

    if (error || !programs) return []

    // Get assignment counts for each program
    const programIds = programs.map((p) => p.id)
    const { data: assignments } = await (supabase
      .from('assignments')
      .select('program_id, status')
      .in('program_id', programIds) as any)

    // Count assignments per program
    const assignmentCounts = new Map<string, number>()
    for (const assignment of (assignments || []) as { program_id: string; status: string }[]) {
      if (options?.status === 'active' && assignment.status !== 'active') continue
      const count = assignmentCounts.get(assignment.program_id) || 0
      assignmentCounts.set(assignment.program_id, count + 1)
    }

    // Get item counts for each program
    const { data: itemCounts } = await supabase
      .from('program_items')
      .select('program_id')
      .in('program_id', programIds)

    const itemCountMap = new Map<string, number>()
    for (const item of itemCounts || []) {
      const count = itemCountMap.get(item.program_id) || 0
      itemCountMap.set(item.program_id, count + 1)
    }

    return programs.map((p) => ({
      id: p.id,
      title: p.title,
      description: p.description,
      visibility: p.visibility,
      isTemplate: p.is_template,
      itemCount: itemCountMap.get(p.id) || 0,
      assignedCount: assignmentCounts.get(p.id) || 0,
      createdAt: p.created_at,
      updatedAt: p.updated_at,
    }))
  },

  /**
   * Quick stats for dashboard cards
   */
  async getQuickStats(providerId: string): Promise<{
    buildProgram: { label: string; action: string }
    activePrograms: { count: number; label: string }
    completedPrograms: { count: number; label: string }
    videoLibrary: { count: number; label: string }
  }> {
    const overview = await this.getDashboardOverview(providerId)

    return {
      buildProgram: {
        label: 'Build Program',
        action: '/dashboard/programs/new',
      },
      activePrograms: {
        count: overview.activePrograms,
        label: 'Active Programs',
      },
      completedPrograms: {
        count: overview.completedPrograms,
        label: 'Completed Programs',
      },
      videoLibrary: {
        count: overview.totalVideos,
        label: 'Video Library',
      },
    }
  },
}
